import * as XLSX from "xlsx";
import { apiUrl } from "../../../config/api";

export class InventoryApiError extends Error {
  constructor(message, { status = 0, code = "INVENTORY_ERROR", details = null } = {}) {
    super(message);
    this.name = "InventoryApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function sessionHeaders(extra = {}) {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  if (!session.sessionKey || !session.username) {
    throw new InventoryApiError("Tu sesión expiró. Vuelve a iniciar sesión.", { status: 401, code: "SESSION_REQUIRED" });
  }
  return {
    Authorization: session.sessionKey,
    Username: session.username,
    ...extra,
  };
}

async function inventoryFetch(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    ...options,
    headers: sessionHeaders({
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    }),
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok || payload?.success === false) {
    throw new InventoryApiError(payload?.message || "No se pudo completar la operación.", {
      status: response.status,
      code: payload?.code,
      details: payload?.details,
    });
  }
  return payload;
}

export async function searchProducts({ query = "", locations = [], page = 1, pageSize = 30, signal } = {}) {
  const params = new URLSearchParams({ q: query.trim(), page: String(page), pageSize: String(pageSize) });
  if (locations.length) params.set("locations", locations.join(","));
  const result = await inventoryFetch(`/products/search?${params.toString()}`, { signal });
  return {
    products: result.products || [],
    pagination: result.pagination || { page: 1, pageSize, total: 0, totalPages: 1 },
  };
}

export function getProduct(productId) {
  return inventoryFetch(`/products/${productId}`);
}

export function getProductHistory(productId) {
  return inventoryFetch(`/products/${productId}/history`);
}

export function updateProduct(productId, values, expectedRevision) {
  return inventoryFetch(`/products/${productId}`, {
    method: "PATCH",
    body: JSON.stringify({ ...values, expectedRevision }),
  });
}

export function adjustProductStock(productId, { newQuantity, reason, expectedRevision }) {
  return inventoryFetch(`/products/${productId}/adjust-stock`, {
    method: "POST",
    body: JSON.stringify({ newQuantity, reason, expectedRevision }),
  });
}

export function addNewProduct(productData) {
  return inventoryFetch("/products/add", {
    method: "POST",
    body: JSON.stringify(productData),
  });
}

export function archiveProduct(productId, reason) {
  return inventoryFetch(`/products/${productId}/archive`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function validateProductBarcode({ barcode, barcodeType, location }) {
  return inventoryFetch("/products/barcodes/validate", {
    method: "POST",
    body: JSON.stringify({ barcode, barcodeType, location }),
  });
}

export function reserveProductBarcode(barcodeType = "code128") {
  return inventoryFetch("/products/barcodes/reserve", {
    method: "POST",
    body: JSON.stringify({ barcodeType }),
  });
}

export async function downloadProductsXLSX(selectedLocations) {
  const params = new URLSearchParams();
  if (selectedLocations.length) params.set("locations", selectedLocations.join(","));
  const products = await inventoryFetch(`/products/download?${params.toString()}`);
  if (!Array.isArray(products) || products.length === 0) {
    throw new InventoryApiError("No hay productos para exportar con estos filtros.", { code: "NO_EXPORT_ROWS" });
  }
  const columns = [
    "code", "brand", "model", "flavor", "packaging", "category", "subcategory",
    "format", "format_unit", "unit_price", "sale_unit", "location", "qty", "unit_cost", "margin",
  ];
  const rows = products.map((product) => Object.fromEntries(columns.map((column) => [
    column,
    column === "code" ? String(product[column] ?? "") : product[column],
  ])));
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: columns });
  for (let row = 2; row <= rows.length + 1; row += 1) {
    const cell = worksheet[`A${row}`];
    if (cell) {
      cell.t = "s";
      cell.v = String(cell.v ?? "");
      cell.z = "@";
    }
  }
  worksheet["!cols"] = columns.map((column) => ({ wch: column === "code" ? 22 : 16 }));
  const instructions = XLSX.utils.aoa_to_sheet([
    ["Plantilla Kakao Inventario", "v2"],
    ["Importante", "Mantén la columna code como texto para conservar ceros iniciales."],
    ["Campos vacíos", "En productos existentes, una celda vacía conserva el valor actual."],
    ["Motivo", "Puedes agregar una columna reason para explicar ajustes de stock."],
  ]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, instructions, "Instrucciones");
  XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");
  XLSX.writeFile(workbook, `inventario-kakao-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export async function parseProductXLSX(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellText: true, cellDates: false });
  const sheet = workbook.Sheets.Productos || workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new InventoryApiError("El archivo no contiene una hoja de productos.", { code: "EMPTY_WORKBOOK" });
  const rows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: null });
  if (!rows.length) throw new InventoryApiError("La hoja de productos está vacía.", { code: "EMPTY_IMPORT" });
  return rows.map((row) => ({
    ...row,
    code: row.code === null || row.code === undefined ? "" : String(row.code).trim(),
    location: row.location === null || row.location === undefined ? null : Number(row.location),
  }));
}

export function previewProductImport(products) {
  return inventoryFetch("/products/upload/preview", {
    method: "POST",
    body: JSON.stringify({ products }),
  });
}

export function commitProductImport(products) {
  return inventoryFetch("/products/upload", {
    method: "POST",
    body: JSON.stringify({ products }),
  });
}
